import {
  accessTokenIsStale,
  clearSession,
  getSession,
  setSession,
} from '@/auth/session';
import type {
  LeadInsightsResponse,
  LeadAnalysisRunResponse,
  LeadNetworkResponse,
  LeadProfileResponse,
  AccountDetail,
  AccountNotification,
  AccountsResponse,
  Alert,
  AvailableFilter,
  MatrixResponse,
  PagedBreakdown,
  AuthResponse,
  OverviewResponse,
  PlanOption,
  DomainComparison,
  JourneysResponse,
  LeadPageResponse,
  LeadPatch,
  LeadRow,
  LeadsSummaryResponse,
  LeadSyncResult,
  PageEngagementResponse,
  RealtimeResponse,
  RumLive,
  RumRollup,
  FeatureRank,
  PlatformAdoption,
  ServicesResponse,
  UnusedFeature,
  TenantTopFeature,
  Presence,
  Seat,
  UserSessions,
  DeviceMix,
  Flows,
  AgentFlows,
  PlatformFlows,
  Collaboration,
  AgentCollaboration,
  Configuration,
  CollaborationDistribution,
  SessionBehaviour,
  SourceHealth,
  SourceResponse,
  SubscriptionDetail,
  Health,
  Risk,
  Onboarding,
  AtRiskAccount,
  Benchmark,
  Cohort,
  ScoreboardRow,
  ExploreCatalogResponse,
  ExploreQueryResponse,
  ExploreSavedView,
  ExploreSpec,
  TenantSearchResponse,
  AdminAuditView,
  AdminUserView,
  AdminWithTemporaryPassword,
} from './types';

const BASE = '/api/v1/controlplane';

/** The one lead filter shape — shared by the summary, the table and the export. */
export interface LeadQuery {
  from?: string;
  to?: string;
  source?: string;
  status?: string;
  country?: string;
  q?: string;
}

/** Only non-empty values are sent, so an unset filter never narrows the server's query. */
function leadParams(siteKey: string, filter: LeadQuery): URLSearchParams {
  const params = new URLSearchParams({ site_key: siteKey });
  for (const [key, value] of Object.entries(filter)) {
    if (value) params.set(key, value);
  }
  return params;
}

/**
 * The RFC 7807 body the console API (and every proxied billing / messaging answer) sends on a 4xx/5xx.
 *
 * `code` is the UPPER_SNAKE application code (`REASON_REQUIRED`, `PROVISIONING_JOB_EXISTS`, …). A 503
 * from a live read carries the labelled-panel fields (`panel: "unavailable"`, `service`, `environment`,
 * `reason`) — docs/api-tenant-ops.md §0. A framework 400 has no `code` but may carry `errors`.
 */
export interface ProblemBody {
  type?: string;
  title?: string;
  detail?: string;
  status?: number;
  code?: string;
  data?: unknown;
  panel?: string;
  service?: string;
  environment?: string;
  reason?: string;
  upstream_status?: number;
  /** On a 503 panel: the service's own UPPER_SNAKE code for its 5xx (e.g. MESSAGING_TENANT_NOT_PROVISIONED). */
  upstream_code?: string;
  errors?: Record<string, string[]>;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** The parsed problem body, when the server sent one. Never a secret — problem bodies carry none. */
    public readonly problem: ProblemBody | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The application error code, verbatim — shown next to the message so the admin can act on it. */
  get code(): string | null {
    return typeof this.problem?.code === 'string' ? this.problem.code : null;
  }

  /**
   * True for the labelled "unavailable" answer of a live read (CP2): the platform service is down,
   * timed out, or the environment cannot reach it. Rendered as a panel, never as a cached or zero value.
   */
  get isUnavailable(): boolean {
    return this.status === 503;
  }
}

/**
 * Single-flight refresh. Several queries can hit a stale token at once; without this
 * they would each POST /auth/refresh, and because the backend ROTATES refresh tokens
 * and treats a reused one as replay, the extra calls would revoke the whole session.
 * One in-flight promise, shared by every caller.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  const session = getSession();
  if (!session) return false;

  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: session.refreshToken }),
      });

      if (!response.ok) {
        clearSession();
        return false;
      }

      setSession((await response.json()) as AuthResponse);
      return true;
    } catch {
      clearSession();
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * The one authenticated JSON call. Exported for the tenant-ops clients (`api/ops.ts`), which share the
 * single-flight refresh and the error model instead of growing a second fetch wrapper.
 */
export async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  if (accessTokenIsStale()) {
    await refreshAccessToken();
  }

  const session = getSession();
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body) headers.set('Content-Type', 'application/json');
  if (session) headers.set('Authorization', `Bearer ${session.accessToken}`);

  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers,
    // Never let the browser heuristic-cache an API body behind the query cache —
    // otherwise only a hard refresh shows new data.
    cache: 'no-store',
  });

  if (response.status === 401 && retry && session) {
    // The token may have been revoked server-side rather than merely expired.
    if (await refreshAccessToken()) {
      return request<T>(path, init, false);
    }
    throw new ApiError(401, 'Session expired. Please sign in again.');
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  if (response.status === 204) return undefined as T;
  // A 201/200 with an empty body (some proxied writes) must not blow up JSON parsing.
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Reads an RFC 7807 ProblemDetails title, falling back to the status text. */
async function readError(response: Response): Promise<string> {
  return (await toApiError(response)).message;
}

/** Parses the problem body ONCE into an {@link ApiError} carrying its code and panel fields. */
async function toApiError(response: Response): Promise<ApiError> {
  let problem: ProblemBody | null = null;
  try {
    const body: unknown = await response.json();
    if (body && typeof body === 'object' && !Array.isArray(body)) {
      problem = body as ProblemBody;
    }
  } catch {
    /* not JSON — fall through */
  }

  const message =
    problem?.title ?? problem?.detail ?? (response.statusText || `Request failed (${response.status})`);
  return new ApiError(response.status, message, problem);
}

/**
 * Downloads a file the API streams (invoice PDFs, CSV exports) with the bearer token.
 *
 * Same reason as the analytics export: a plain `<a href>` would not carry the token and would 401. The
 * file is never kept — it is handed to a synthetic anchor and the object URL is revoked.
 */
export async function downloadFile(path: string, fallbackName: string): Promise<string> {
  if (accessTokenIsStale()) await refreshAccessToken();
  const session = getSession();

  const response = await fetch(`${BASE}${path}`, {
    headers: session ? { Authorization: `Bearer ${session.accessToken}` } : {},
    cache: 'no-store',
  });
  if (!response.ok) throw await toApiError(response);

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const captured = match?.[1];
  const name = captured ? decodeURIComponent(captured) : fallbackName;

  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return name;
}

export const api = {
  async login(email: string, password: string): Promise<AuthResponse> {
    const response = await fetch(`${BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new ApiError(response.status, await readError(response));
    }

    return (await response.json()) as AuthResponse;
  },

  async logout(): Promise<void> {
    const session = getSession();
    if (session) {
      // Best-effort: local state is cleared regardless, so a failed call can never
      // strand the user in a half-logged-out state.
      await fetch(`${BASE}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: session.refreshToken }),
      }).catch(() => undefined);
    }
    clearSession();
  },

  /** The signed-in admin's own password; answers a fresh session (every other one is signed out). */
  changePassword: (currentPassword: string, newPassword: string) =>
    request<AuthResponse>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    }),

  /** The console's own admins (owner only). A temporary password comes back ONCE, on create and reset. */
  admins: {
    list: () => request<{ admins: AdminUserView[] }>('/admin-users'),
    create: (body: { email: string; display_name: string; role: string }) =>
      request<AdminWithTemporaryPassword>('/admin-users', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: { display_name?: string; role?: string; enabled?: boolean }) =>
      request<AdminUserView>(`/admin-users/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
    resetPassword: (id: string) =>
      request<AdminWithTemporaryPassword>(`/admin-users/${encodeURIComponent(id)}/reset-password`, { method: 'POST' }),
    unlock: (id: string) => request<AdminUserView>(`/admin-users/${encodeURIComponent(id)}/unlock`, { method: 'POST' }),
    audit: (before?: number | null) =>
      request<{ entries: AdminAuditView[]; next_before: number | null }>(`/admin-users/audit?limit=50${before ? `&before=${before}` : ''}`),
  },

  /** The search box: tenants by name, owner e-mail or id prefix (replica, analytics:read). */
  searchTenants: (query: string) =>
    request<TenantSearchResponse>(`/accounts/search?q=${encodeURIComponent(query)}&limit=8`),

  listSources: () => request<SourceResponse[]>('/sources'),

  /** Owner (source:manage). `config` holds non-secret provider ids only — credentials are server settings. */
  createSource: (body: { provider: string; site_key: string; display_name: string; reporting_time_zone: string; config: Record<string, string> }) =>
    request<SourceResponse>('/sources', { method: 'POST', body: JSON.stringify(body) }),

  updateSource: (id: string, body: { display_name?: string; reporting_time_zone?: string; enabled?: boolean; config?: Record<string, string> }) =>
    request<SourceResponse>(`/sources/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),

  overview: (siteKey: string, from?: string, to?: string) => {
    const params = new URLSearchParams({ site_key: siteKey });
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return request<OverviewResponse>(`/analytics/overview?${params}`);
  },

  accounts: (includeInternal = false) =>
    request<AccountsResponse>(`/accounts?include_internal=${includeInternal}`),

  account: (tenantId: string) =>
    request<AccountDetail>(`/accounts/${encodeURIComponent(tenantId)}`),

  accountNotifications: (includeRead = false, limit = 50) =>
    request<AccountNotification[]>(`/accounts/notifications?limit=${limit}${includeRead ? '&include_read=true' : ''}`),

  /** The bell's badge — the true count (the list stops at its limit). */
  unreadNotificationCount: () => request<{ unread: number }>('/accounts/notifications/unread-count'),

  /** Marks every unread notification read — for every admin (read state is shared). */
  ackAllAccountNotifications: () => request<{ acknowledged: number }>('/accounts/notifications/ack-all', { method: 'POST' }),

  ackAccountNotification: (id: string) =>
    request<void>(`/accounts/notifications/${encodeURIComponent(id)}/ack`, { method: 'POST' }),

  services: () => request<ServicesResponse>('/services'),

  /**
   * Feature usage — "which feature is used most" at agent, tenant and platform level.
   *
   * Grouped as a namespace like `leads` and `subscription`, so the endpoints read as one
   * subject rather than loose thunks on the root object.
   */
  telemetry: {
    /** Top features for one account. Omitting the range asks the server for its default 30 days. */
    tenantFeatures: (tenantId: string, from?: string, to?: string) => {
      const p = new URLSearchParams();
      if (from) p.set('from', from);
      if (to) p.set('to', to);
      const qs = p.toString();
      return request<FeatureRank[]>(
        `/accounts/${encodeURIComponent(tenantId)}/features${qs ? `?${qs}` : ''}`,
      );
    },

    /** One agent's features. Gated on telemetry:agent_detail — a viewer gets 403 here. */
    agentFeatures: (tenantId: string, userId: string, from?: string, to?: string) => {
      const p = new URLSearchParams();
      if (from) p.set('from', from);
      if (to) p.set('to', to);
      const qs = p.toString();
      return request<FeatureRank[]>(
        `/accounts/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/features${qs ? `?${qs}` : ''}`,
      );
    },

    /** Adoption across every tenant. Internal environments excluded unless asked for. */
    platformFeatures: (includeInternal = false, from?: string, to?: string) => {
      const p = new URLSearchParams({ include_internal: String(includeInternal) });
      if (from) p.set('from', from);
      if (to) p.set('to', to);
      return request<PlatformAdoption>(`/telemetry/features?${p}`);
    },

    unusedFeatures: () => request<UnusedFeature[]>('/telemetry/features/unused'),

    /**
     * Each tenant's most-used feature over the server's default 30 days — the accounts list's
     * "Top feature" column. Joined client-side by tenant_id; a tenant with no usage is absent.
     */
    topFeaturePerTenant: () => request<TenantTopFeature[]>('/telemetry/features/top-per-tenant'),
  },

  /**
   * Presence, seats and session history.
   *
   * Every response carries `synced_at`. These are replica reads on the collector's cadence, not a
   * live view of the platform, and the surfaces that render them say so.
   */
  presence: {
    tenant: (tenantId: string) =>
      request<Presence>(`/accounts/${encodeURIComponent(tenantId)}/presence`),

    seats: (tenantId: string) =>
      request<Seat[]>(`/accounts/${encodeURIComponent(tenantId)}/seats`),

    /** One person's sign-in history. Gated on telemetry:agent_detail — a viewer gets 403 here. */
    userSessions: (tenantId: string, userId: string, limit?: number) => {
      const qs = limit ? `?limit=${limit}` : '';
      return request<UserSessions>(
        `/accounts/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/sessions${qs}`,
      );
    },

    tenantDevices: (tenantId: string) =>
      request<DeviceMix>(`/accounts/${encodeURIComponent(tenantId)}/devices`),

    platformDevices: (includeInternal = false) =>
      request<DeviceMix>(`/telemetry/devices?include_internal=${includeInternal}`),
  },

  /**
   * Paths and flows — what sequence of features an agent walks.
   *
   * The path of CHANGES, not of use: it derives from write actions, so reading leaves no trace.
   * Every surface rendering these must say so.
   */
  paths: {
    tenant: (tenantId: string) =>
      request<Flows>(`/accounts/${encodeURIComponent(tenantId)}/paths`),

    /** One agent's own sequence. Gated on telemetry:agent_detail — a viewer gets 403 here. */
    agent: (tenantId: string, userId: string) =>
      request<AgentFlows>(
        `/accounts/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/paths`,
      ),

    platform: (includeInternal = false) =>
      request<PlatformFlows>(`/telemetry/paths?include_internal=${includeInternal}`),
  },

  /**
   * Collaboration and configuration — is this a team, and how much have they built?
   *
   * Every count is nullable and the `*_collected` flags say why. Null is NOT COLLECTED; zero is
   * collected and genuinely none. Rendering the two the same turns a collection gap into a finding
   * about the customer.
   */
  collaboration: {
    tenant: (tenantId: string) =>
      request<Collaboration>(`/accounts/${encodeURIComponent(tenantId)}/collaboration`),

    configuration: (tenantId: string) =>
      request<Configuration>(`/accounts/${encodeURIComponent(tenantId)}/configuration`),

    /** One agent's sharing. Gated on telemetry:agent_detail — a viewer gets 403 here. */
    agent: (tenantId: string, userId: string) =>
      request<AgentCollaboration>(
        `/accounts/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/collaboration`,
      ),

    distribution: (includeInternal = false) =>
      request<CollaborationDistribution>(
        `/telemetry/collaboration?include_internal=${includeInternal}`),
  },

  /**
   * Health, risk, onboarding and benchmarks — the judgements.
   *
   * Everything here is explainable from its own response: a total always ships with the components
   * that produced it and the ones that could not be measured, and every risk flag ships with the
   * number that raised it. A score nobody can explain gets ignored within a month, and deserves to.
   */
  scores: {
    /**
     * 404 when this tenant has never been scored, which the caller must NOT render as zero — a
     * healthy account shown as failing because nobody has run the numbers yet is the worst answer
     * this endpoint could give.
     */
    health: (tenantId: string) =>
      request<Health>(`/accounts/${encodeURIComponent(tenantId)}/health`),

    risk: (tenantId: string) =>
      request<Risk>(`/accounts/${encodeURIComponent(tenantId)}/risk`),

    onboarding: (tenantId: string) =>
      request<Onboarding>(`/accounts/${encodeURIComponent(tenantId)}/onboarding`),

    /** The work queue, revenue-ordered. What makes this service a queue rather than a dashboard. */
    atRisk: (includeInternal = false, limit = 100) =>
      request<AtRiskAccount[]>(
        `/telemetry/at-risk?include_internal=${includeInternal}&limit=${limit}`),

    /**
     * Latest score per tenant, for the accounts list.
     *
     * Unscored tenants are absent, so joining on `tenant_id` yields undefined for them — which the
     * list must render as "not scored", never as a zero.
     */
    scoreboard: () => request<ScoreboardRow[]>('/telemetry/scores'),

    /**
     * Platform percentiles.
     *
     * No `include_internal` parameter, deliberately — most tenants here ARE environments, and a
     * median that counts them describes our own seed scripts rather than any customer.
     */
    benchmarks: () => request<Benchmark[]>('/telemetry/benchmarks'),

    cohorts: (includeInternal = false) =>
      request<Cohort[]>(`/telemetry/cohorts?include_internal=${includeInternal}`),
  },

  /** 2-D grid for the heatmap. Needs GA4 cross-tab rows. */
  matrix: (siteKey: string, rows: string, cols: string, metric: string, from?: string, to?: string) => {
    const p = new URLSearchParams({ site_key: siteKey, rows, cols, metric });
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    return request<MatrixResponse>(`/analytics/matrix?${p}`);
  },

  /** Paginated, optionally cross-filtered breakdown — not bound by the overview's top-20 cap. */
  breakdown: (
    siteKey: string,
    dimension: string,
    opts: { page?: number; perPage?: number; filterDimension?: string; filterValue?: string; from?: string; to?: string } = {},
  ) => {
    const p = new URLSearchParams({ site_key: siteKey, dimension });
    if (opts.page) p.set('page', String(opts.page));
    if (opts.perPage) p.set('per_page', String(opts.perPage));
    if (opts.filterDimension && opts.filterValue) {
      p.set('filter_dimension', opts.filterDimension);
      p.set('filter_value', opts.filterValue);
    }
    if (opts.from) p.set('from', opts.from);
    if (opts.to) p.set('to', opts.to);
    return request<PagedBreakdown>(`/analytics/breakdown?${p}`);
  },

  /**
   * Downloads the spreadsheet export.
   *
   * Cannot be an `<a href>` or `window.open`: the endpoint requires the bearer token, which a
   * plain navigation does not carry — it would 401. So the file is fetched, turned into a blob and
   * handed to a synthetic anchor. Returns the filename the server chose, which is where the date
   * range lives.
   */
  async exportAnalytics(
    siteKey: string,
    opts: { dimension?: string; filterDimension?: string; filterValue?: string; from?: string; to?: string } = {},
  ): Promise<string> {
    const p = new URLSearchParams({ site_key: siteKey });
    if (opts.dimension) p.set('dimension', opts.dimension);
    if (opts.filterDimension && opts.filterValue) {
      p.set('filter_dimension', opts.filterDimension);
      p.set('filter_value', opts.filterValue);
    }
    if (opts.from) p.set('from', opts.from);
    if (opts.to) p.set('to', opts.to);

    if (accessTokenIsStale()) await refreshAccessToken();
    const session = getSession();

    const response = await fetch(`${BASE}/analytics/export?${p}`, {
      headers: session ? { Authorization: `Bearer ${session.accessToken}` } : {},
      cache: 'no-store',
    });
    if (!response.ok) throw new ApiError(response.status, await readError(response));

    // Prefer the server's filename — it carries the range, and a file called "export.xls" in a
    // downloads folder six weeks later is unreadable.
    const disposition = response.headers.get('Content-Disposition') ?? '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    // `match?.[1]` rather than `match ?`: under noUncheckedIndexedAccess a capture group is
    // string | undefined even when the match succeeded, and an empty group would otherwise reach
    // decodeURIComponent as undefined.
    const captured = match?.[1];
    const name = captured
      ? decodeURIComponent(captured)
      : `tavi_analytics_${opts.from ?? ''}_${opts.to ?? ''}.xls`;

    const url = URL.createObjectURL(await response.blob());
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Revoking immediately cancels the download in Safari; one frame is enough everywhere else.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return name;
  },

  analyticsFilters: (siteKey: string, from?: string, to?: string) => {
    const p = new URLSearchParams({ site_key: siteKey });
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    return request<AvailableFilter[]>(`/analytics/filters?${p}`);
  },

  /** Time on page: GA4 and first-party side by side, never blended. */
  pageEngagement: (siteKey: string, from?: string, to?: string, limit = 50) => {
    const p = new URLSearchParams({ site_key: siteKey, limit: String(limit) });
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    return request<PageEngagementResponse>(`/analytics/page-engagement?${p}`);
  },

  /** The three domains side by side. GA4-only — Clarity has no host dimension. */
  domains: (siteKey: string, from?: string, to?: string) => {
    const p = new URLSearchParams({ site_key: siteKey });
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    return request<DomainComparison>(`/analytics/domains?${p}`);
  },

  /** Page sequences from first-party RUM — neither GA4 nor Clarity can answer this. */
  journeys: (
    siteKey: string,
    opts: { grain?: 'day' | 'month'; from?: string; to?: string; country?: string; hostname?: string; minSteps?: number; limit?: number } = {},
  ) => {
    const p = new URLSearchParams({ site_key: siteKey, grain: opts.grain ?? 'day' });
    if (opts.from) p.set('from', opts.from);
    if (opts.to) p.set('to', opts.to);
    if (opts.country) p.set('country', opts.country);
    if (opts.hostname) p.set('hostname', opts.hostname);
    if (opts.minSteps) p.set('min_steps', String(opts.minSteps));
    if (opts.limit) p.set('limit', String(opts.limit));
    return request<JourneysResponse>(`/rum/journeys?${p}`);
  },

  /** How long visits last, where they start and end, and why they ended. First-party only. */
  rumSessions: (
    siteKey: string,
    opts: { grain?: 'day' | 'month'; from?: string; to?: string; country?: string; hostname?: string; limit?: number } = {},
  ) => {
    const p = new URLSearchParams({ site_key: siteKey, grain: opts.grain ?? 'day' });
    if (opts.from) p.set('from', opts.from);
    if (opts.to) p.set('to', opts.to);
    if (opts.country) p.set('country', opts.country);
    if (opts.hostname) p.set('hostname', opts.hostname);
    if (opts.limit) p.set('limit', String(opts.limit));
    return request<SessionBehaviour>(`/rum/sessions?${p}`);
  },

  /** First-party live snapshot. One-shot; `useRumLive` streams it instead. */
  rumLive: (siteKey: string, pagePath?: string) => {
    const p = new URLSearchParams({ site_key: siteKey });
    if (pagePath) p.set('page_path', pagePath);
    return request<RumLive>(`/rum/live?${p}`);
  },

  /** Stored day/month rollups — durable, and deliberately not realtime. */
  rumRollups: (siteKey: string, grain: 'day' | 'month', from?: string, to?: string) => {
    const p = new URLSearchParams({ site_key: siteKey, grain });
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    return request<RumRollup[]>(`/rum/rollups?${p}`);
  },

  /**
   * The lead pipeline. Every read takes the SAME filter object, so the KPIs, the table and the
   * export can never describe different sets of rows.
   */
  leads: {
    /** Stored analyses. Reads the TABLE — never triggers a model call. */
    insights: (siteKey: string) =>
      request<LeadInsightsResponse>(`/leads/insights?site_key=${encodeURIComponent(siteKey)}`),

    /** Spends from a scarce shared quota, hence a management action rather than a read. */
    analyse: (siteKey: string) =>
      request<LeadAnalysisRunResponse>(
        `/leads/analyse?site_key=${encodeURIComponent(siteKey)}`, { method: "POST" }),

    summary: (siteKey: string, filter: LeadQuery = {}) =>
      request<LeadsSummaryResponse>(`/leads/summary?${leadParams(siteKey, filter)}`),

    /**
     * One lead in full — score workings, timeline, peer percentiles and lookalikes.
     *
     * Deliberately NOT assembled from four calls on the client: the percentiles and the "related"
     * list are both computed against one cohort, and separate requests would each pick their own.
     */
    profile: (id: string) =>
      request<LeadProfileResponse>(`/leads/${encodeURIComponent(id)}`),

    /**
     * The similarity graph over the SAME filtered rows the table is showing.
     *
     * `max_nodes` is a cost knob, not a page size: betweenness is O(V·E), so the server clamps it.
     */
    network: (siteKey: string, filter: LeadQuery = {}, maxNodes?: number, minWeight?: number) => {
      const params = leadParams(siteKey, filter);
      if (maxNodes) params.set('max_nodes', String(maxNodes));
      if (minWeight) params.set('min_weight', String(minWeight));
      return request<LeadNetworkResponse>(`/leads/network?${params}`);
    },

    list: (siteKey: string, filter: LeadQuery = {}, page = 1, perPage = 25) => {
      const params = leadParams(siteKey, filter);
      params.set('page', String(page));
      params.set('per_page', String(perPage));
      return request<LeadPageResponse>(`/leads?${params}`);
    },

    patch: (id: string, body: LeadPatch) =>
      request<LeadRow>(`/leads/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    /** Pulls both upstreams now. Returns one result per source — a partial failure stays visible. */
    sync: (siteKey: string) =>
      request<LeadSyncResult[]>(`/leads/sync?site_key=${encodeURIComponent(siteKey)}`, {
        method: 'POST',
      }),

    /**
     * CSV of the filtered rows. Fetched as a blob for the same reason as the analytics export:
     * the endpoint needs the bearer token, which a plain navigation would not carry.
     */
    async export(siteKey: string, filter: LeadQuery = {}): Promise<string> {
      if (accessTokenIsStale()) await refreshAccessToken();
      const session = getSession();

      const response = await fetch(`${BASE}/leads/export?${leadParams(siteKey, filter)}`, {
        headers: session ? { Authorization: `Bearer ${session.accessToken}` } : {},
        cache: 'no-store',
      });
      if (!response.ok) throw new ApiError(response.status, await readError(response));

      const name = `tavi_leads_${filter.from ?? ''}_${filter.to ?? ''}.csv`;
      const url = URL.createObjectURL(await response.blob());
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = name;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return name;
    },
  },

  /**
   * The explore surface (phase 6): dimension × measure × range × breakdown over the cross-tenant
   * rollups. The catalog drives the dropdowns; the server chooses the chart; the row cap comes
   * back in every response so the UI can say when a result was cut.
   */
  explore: {
    catalog: () => request<ExploreCatalogResponse>('/explore/catalog'),

    query: (spec: ExploreSpec) =>
      request<ExploreQueryResponse>('/explore/query', {
        method: 'POST',
        body: JSON.stringify(spec),
      }),

    listViews: () => request<ExploreSavedView[]>('/explore/views'),

    saveView: (name: string, spec: ExploreSpec) =>
      request<ExploreSavedView>('/explore/views', {
        method: 'POST',
        body: JSON.stringify({ name, spec }),
      }),

    deleteView: (id: string) =>
      request<void>(`/explore/views/${encodeURIComponent(id)}`, { method: 'DELETE' }),

    /** CSV via blob + synthetic anchor — the endpoint needs the bearer token. */
    async export(spec: ExploreSpec): Promise<string> {
      if (accessTokenIsStale()) await refreshAccessToken();
      const session = getSession();

      const response = await fetch(`${BASE}/explore/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session ? { Authorization: `Bearer ${session.accessToken}` } : {}),
        },
        body: JSON.stringify(spec),
        cache: 'no-store',
      });
      if (!response.ok) throw new ApiError(response.status, await readError(response));

      // Prefer the server's filename — it carries the range.
      const disposition = response.headers.get('Content-Disposition') ?? '';
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
      const captured = match?.[1];
      const name = captured
        ? decodeURIComponent(captured)
        : `tavi_explore_${spec.from}_${spec.to}.csv`;

      const url = URL.createObjectURL(await response.blob());
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = name;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return name;
    },
  },

  alerts: (siteKey: string) =>
    request<Alert[]>(`/analytics/alerts?site_key=${encodeURIComponent(siteKey)}`),

  health: (siteKey: string) =>
    request<SourceHealth[]>(`/analytics/health?site_key=${encodeURIComponent(siteKey)}`),

  /**
   * Live snapshot. Returns null on 204 — a site with no GA4 source is a normal configuration,
   * not an error, and the card simply does not render.
   */
  realtime: (siteKey: string) =>
    request<RealtimeResponse | undefined>(
      `/analytics/realtime?site_key=${encodeURIComponent(siteKey)}`,
    ).then((value) => value ?? null),

  ingest: (sourceId: string, date?: string) => {
    const params = date ? `?date=${encodeURIComponent(date)}` : '';
    return request<{ status: string; rows_written: number; skip_reason: string | null; error: string | null }>(
      `/sources/${sourceId}/ingest${params}`,
      { method: 'POST' },
    );
  },

  /**
   * Subscription ops.
   *
   * Unlike every other call here, these reach anyware.iam (through the control plane). They are the
   * only calls on the Accounts pages that fail when the platform is down — a write has to land in the
   * source of truth, and nothing is queued, so a 503 means "it did not happen".
   *
   * Every mutation returns the UPDATED subscription, so the caller replaces its cache entry rather
   * than re-fetching.
   */
  subscription: {
    plans: (tenantId: string) =>
      request<PlanOption[]>(`/accounts/${encodeURIComponent(tenantId)}/subscription/plans`),

    /** 404 → the tenant has no subscription upstream; IAM's gate is failing open for it. */
    get: (tenantId: string) =>
      request<SubscriptionDetail>(`/accounts/${encodeURIComponent(tenantId)}/subscription`),

    changePlan: (
      tenantId: string,
      body: { plan_code: string; billing_cycle?: string; price_minor?: number; currency?: string },
    ) =>
      request<SubscriptionDetail>(`/accounts/${encodeURIComponent(tenantId)}/subscription/plan`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /** `seat_limit: null` CLEARS the limit (unenforced). It never means zero. */
    setSeats: (tenantId: string, seatLimit: number | null) =>
      request<SubscriptionDetail>(`/accounts/${encodeURIComponent(tenantId)}/subscription/seats`, {
        method: 'POST',
        body: JSON.stringify({ seat_limit: seatLimit }),
      }),

    extendTrial: (tenantId: string, days: number) =>
      request<SubscriptionDetail>(`/accounts/${encodeURIComponent(tenantId)}/subscription/extend-trial`, {
        method: 'POST',
        body: JSON.stringify({ days }),
      }),

    setOverride: (
      tenantId: string,
      body: { app_key: string; effect: 'grant' | 'deny'; reason?: string; expires_at?: string },
    ) =>
      request<SubscriptionDetail>(`/accounts/${encodeURIComponent(tenantId)}/subscription/overrides`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    clearOverride: (tenantId: string, appKey: string) =>
      request<SubscriptionDetail>(
        `/accounts/${encodeURIComponent(tenantId)}/subscription/overrides/${encodeURIComponent(appKey)}`,
        { method: 'DELETE' },
      ),

    /** Suspend / reactivate / cancel — one call, because IAM has one state machine. */
    changeStatus: (tenantId: string, status: string, reason?: string) =>
      request<SubscriptionDetail>(`/accounts/${encodeURIComponent(tenantId)}/subscription/status`, {
        method: 'POST',
        body: JSON.stringify({ status, reason }),
      }),
  },
};
